import type { TournamentSettings } from '@ctd/core';
import { useState, type FormEvent } from 'react';
import {
  DEFAULT_TIEBREAKS,
  engineOptions,
  settingsFromForm,
  tiebreakCatalogue,
  tiebreakPresets,
  type FormErrors,
  type SettingsForm as Form,
} from '../lib/settings';
import { SelectField, TextField } from './ui';

interface Props {
  initial: Form;
  /** Current settings when editing (keeps fields the form does not show). */
  base?: TournamentSettings;
  /** Rounds already paired: the round count cannot go below it. */
  minRounds?: number;
  submitLabel: string;
  onSubmit: (settings: TournamentSettings) => void | Promise<void>;
}

export function SettingsForm({ initial, base, minRounds = 0, submitLabel, onSubmit }: Props) {
  const [form, setForm] = useState<Form>(initial);
  const [errors, setErrors] = useState<FormErrors>({});
  const [busy, setBusy] = useState(false);
  const set =
    <K extends keyof Form>(k: K) =>
    (v: string) =>
      setForm((f) => ({ ...f, [k]: v as Form[K] }));

  const systems = engineOptions().map((e) => ({ value: e.id, label: e.name }));
  if (!systems.some((s) => s.value === form.system)) {
    systems.unshift({ value: form.system, label: `${form.system} (no engine yet)` });
  }
  const pointOptions = [
    { value: 'standard', label: 'Standard: win 1, draw ½, loss 0' },
    { value: '3-1-0', label: 'Football: win 3, draw 1, loss 0' },
  ];
  if (form.pointSystem === 'custom') {
    pointOptions.push({ value: 'custom', label: 'Custom (from the imported file)' });
  }

  async function submit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    const result = settingsFromForm(form, base, minRounds);
    setErrors(result.errors);
    if (result.settings === null) {
      // Send focus to the first field with a problem.
      const first = e.currentTarget.querySelector<HTMLElement>('[aria-invalid="true"]');
      first?.focus();
      return;
    }
    setBusy(true);
    try {
      await onSubmit(result.settings);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="form-grid" onSubmit={(e) => void submit(e)} noValidate>
      <TextField
        className="span-2"
        label="Tournament name"
        value={form.name}
        onValue={set('name')}
        error={errors.name}
        required
        autoComplete="off"
      />
      <TextField label="City" value={form.city} onValue={set('city')} autoComplete="off" />
      <TextField
        label="Chief arbiter"
        value={form.chiefArbiter}
        onValue={set('chiefArbiter')}
        autoComplete="off"
      />
      <TextField label="Start date" type="date" value={form.startDate} onValue={set('startDate')} />
      <TextField
        label="End date"
        type="date"
        value={form.endDate}
        onValue={set('endDate')}
        error={errors.endDate}
      />
      <TextField
        label="Rounds"
        type="number"
        inputMode="numeric"
        min={Math.max(1, minRounds)}
        max={99}
        value={form.rounds}
        onValue={set('rounds')}
        error={errors.rounds}
        required
      />
      <SelectField
        label="Pairing system"
        value={form.system}
        onValue={set('system')}
        options={systems}
        error={errors.system}
      />
      <SelectField
        label="Point system"
        value={form.pointSystem}
        onValue={set('pointSystem')}
        options={pointOptions}
      />
      <SelectField
        label="Colour of the top seed in round 1"
        value={form.initialColour}
        onValue={set('initialColour')}
        options={[
          { value: 'w', label: 'White' },
          { value: 'b', label: 'Black' },
        ]}
      />
      <SelectField
        label="Acceleration"
        value={form.acceleration}
        onValue={set('acceleration')}
        options={[
          { value: 'none', label: 'None' },
          { value: 'baku', label: 'Baku acceleration (FIDE C.04.7)' },
        ]}
      />
      <TextField
        className="span-2"
        label="Tiebreaks, in order"
        value={form.tiebreaks}
        onValue={set('tiebreaks')}
        error={errors.tiebreaks}
        autoComplete="off"
        spellCheck={false}
        hint={
          <>
            Comma separated MTB26 codes, e.g. <code>{DEFAULT_TIEBREAKS}</code>. Add <code>/C1</code>{' '}
            to cut the lowest value or <code>/M1</code> for the median.
          </>
        }
      />
      <SelectField
        label="Fill in a preset"
        value=""
        onValue={(v) => {
          if (v !== '') set('tiebreaks')(v);
        }}
        options={[
          { value: '', label: 'Choose a preset…' },
          ...tiebreakPresets().map((p) => ({ value: p.text, label: `${p.name}: ${p.text}` })),
        ]}
        hint="Common practice, not FIDE recommendations (C.07 has no default list)."
      />
      <details className="span-2 tiebreak-codes">
        <summary>All tiebreak codes</summary>
        <dl className="legend">
          {tiebreakCatalogue().map((t) => (
            <div key={t.code}>
              <dt>{t.code}</dt>
              <dd>{t.name}</dd>
            </div>
          ))}
        </dl>
      </details>
      <div className="form-actions span-2">
        <button type="submit" className="button primary" disabled={busy}>
          {submitLabel}
        </button>
      </div>
    </form>
  );
}
